import { useEffect, useRef } from "react";

import every from "lodash/every";
import isNil from "lodash/isNil";

import { router } from "@/crossPlatform/expo-router";
import { useUnmount } from "@/hooks/useUnmount";
import useSinglyConcurrentAsyncAction from "@/hooks/util/useSinglyConcurrentAsyncAction";
import useAppMonitor from "@/services/AppMonitor";
import { useAppSelector } from "@/store/hooks";
import {
  selectTaskDetailsData,
  useTaskDetailsModalDispatch,
} from "@/store/reducers/modals/taskDetailsModal";
import MedicalEvaluationTaskDetails from "@/ui/components/tasks/MedicalEvaluationTaskDetails";
import TaskDetailsModalWrapper, {
  FooterActionLabel,
} from "@/ui/components/tasks/TaskDetailsModalWrapper";
import { FullScreenSpinner } from "@/ui/core";
import { openUrl } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";
import {
  extractLinksFromMarkdown,
  getEnglishVersionLink,
} from "@/utils/string.util";

const Modal = () => {
  const { captureException } = useAppMonitor();
  const renderCountRef = useRef<number>(1);
  useEffect(() => {
    renderCountRef.current = renderCountRef.current + 1;
  });
  const { execute, isPending: isLoading } = useSinglyConcurrentAsyncAction();

  const { resetTaskDetailsData } = useTaskDetailsModalDispatch();
  useUnmount({ onUnmount: resetTaskDetailsData });

  const taskDetailsData = useAppSelector(selectTaskDetailsData);

  const { description, duration, patientName, title } = taskDetailsData || {};

  if (
    isNil(title) ||
    isNil(description) ||
    isNil(duration) ||
    !every([title, description, duration])
  ) {
    // capture renderCount in error message to enable better debugging and
    // to distinguish this happening on initial render, a true critical error,
    // versus on subsequent render, which means initial render succeeded and something just goes wrong later
    captureException(
      new Error(
        `MedicalEvaluationTaskDetails missing required params on render #${renderCountRef.current}`,
      ),
    );
    if (isWeb()) {
      router.dismiss();
    }
    return <FullScreenSpinner />;
  }

  const links = extractLinksFromMarkdown(description);

  // For the button we want to open the English version of the form
  const englishVersionLink = getEnglishVersionLink(links);

  const onSignForm = async () => {
    if (!englishVersionLink) return;
    await openUrl({
      onError: (error?: Error) => {
        captureException(new Error("Failed to open URL", { cause: error }));
      },
      url: englishVersionLink,
    });
  };
  const onSignFormClick = async () => execute(onSignForm);

  return (
    <TaskDetailsModalWrapper
      footerRightButtonLabel={FooterActionLabel.SignDocument}
      isLoading={isLoading}
      onFooterRightButtonPress={onSignFormClick}
    >
      <MedicalEvaluationTaskDetails
        description={description}
        duration={duration}
        patientName={patientName}
        title={title}
      />
    </TaskDetailsModalWrapper>
  );
};

export default Modal;
